import { useEffect, useState } from "react";
import apiClient from "@/services/apiClient";
import { Target, Eye, Award, Users } from "lucide-react";

export default function About() {
  const [trainers, setTrainers] = useState([]);
  const [trainersLoading, setTrainersLoading] = useState(true);
  const [trainersError, setTrainersError] = useState("");

  useEffect(() => {
    apiClient
      .get("/Trainer/GetPublicTrainers")
      .then((response) => setTrainers(Array.isArray(response.data) ? response.data : []))
      .catch(() => setTrainersError("Trainer information is temporarily unavailable."))
      .finally(() => setTrainersLoading(false));
  }, []);

  const getInitials = (trainer) =>
    `${trainer.firstName?.[0] || ""}${trainer.lastName?.[0] || ""}`.toUpperCase();

  return (
    <div>
      <section className="bg-[#0A2540] text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-400 mb-3">About us</div>
          <h1 className="text-4xl lg:text-6xl font-display font-extrabold max-w-3xl leading-tight">Victoria's most trusted White Card provider.</h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300">We've trained over 2,800 construction workers across Melbourne since 2018 — with a 97% same-day pass rate and a review score of 4.9/5.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-20 grid md:grid-cols-2 gap-12">
        <div className="wc-card p-8">
          <div className="w-11 h-11 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-4"><Target className="w-5 h-5" /></div>
          <h2 className="text-2xl font-display font-extrabold text-[#0A2540] mb-3">Our mission</h2>
          <p className="text-slate-600 leading-relaxed">To make workplace safety training in the Australian construction industry accessible, engaging, and genuinely useful — not just a box-tick.</p>
        </div>
        <div className="wc-card p-8">
          <div className="w-11 h-11 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-4"><Eye className="w-5 h-5" /></div>
          <h2 className="text-2xl font-display font-extrabold text-[#0A2540] mb-3">Our vision</h2>
          <p className="text-slate-600 leading-relaxed">Zero preventable incidents on Australian construction sites — starting with the very first shift.</p>
        </div>
      </section>

      <section className="bg-slate-50 py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-2xl mb-10">
            <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Accreditation</div>
            <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540]">Fully accredited and compliant</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="wc-card"><Award className="w-8 h-8 text-orange-500 mb-3" /><div className="font-display font-extrabold text-[#0A2540]">RTO #45698</div><p className="text-sm text-slate-600 mt-1">Registered Training Organisation on the National VET Register.</p></div>
            <div className="wc-card"><Award className="w-8 h-8 text-orange-500 mb-3" /><div className="font-display font-extrabold text-[#0A2540]">WorkSafe VIC</div><p className="text-sm text-slate-600 mt-1">Approved to deliver CPCCWHS1001 across Victoria.</p></div>
            <div className="wc-card"><Award className="w-8 h-8 text-orange-500 mb-3" /><div className="font-display font-extrabold text-[#0A2540]">ASQA compliant</div><p className="text-sm text-slate-600 mt-1">Fully compliant with Australian Skills Quality Authority standards.</p></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="max-w-2xl mb-10">
          <div className="text-sm font-semibold tracking-wide uppercase text-orange-600 mb-2">Meet the team</div>
          <h2 className="text-3xl lg:text-4xl font-display font-extrabold text-[#0A2540]">Experienced trainers from real construction sites</h2>
          <p className="mt-3 text-slate-600">Meet the qualified trainers currently delivering our courses.</p>
        </div>

        {trainersLoading && (
          <div className="wc-card text-center text-slate-500" data-testid="trainers-loading">Loading trainers...</div>
        )}

        {!trainersLoading && trainersError && (
          <div className="wc-card text-center text-slate-600" data-testid="trainers-error">{trainersError}</div>
        )}

        {!trainersLoading && !trainersError && trainers.length === 0 && (
          <div className="wc-card text-center text-slate-500" data-testid="trainers-empty">Trainer profiles will be available soon.</div>
        )}

        {!trainersLoading && !trainersError && trainers.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="trainers-grid">
            {trainers.map((trainer) => (
              <div key={trainer.trainerId} className="wc-card text-center">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-slate-100 bg-[#0A2540] text-white flex items-center justify-center text-2xl font-display font-extrabold">
                  {getInitials(trainer)}
                </div>
                <div className="font-display font-extrabold text-[#0A2540]">
                  {trainer.firstName} {trainer.lastName}
                </div>
                <div className="text-xs text-slate-500 mt-1 mb-3">
                  {trainer.qualification || "Qualified construction safety trainer"}
                </div>
                <div className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full">
                  <Users className="w-3 h-3" />
                  {trainer.classCount || 0} classes delivered
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
